/**
 * Phase 101fx (Feb 2026) — Vague 1 : Page /parties (carnet central relationnel).
 *
 * Écran d'exploration de la fondation `parties` (source de vérité future).
 * En vague 1 : lecture seule sur les collections sources, création/édition
 * de Parties standalone, backfill idempotent depuis les 7 modules d'origine.
 *
 * Différence clé avec /contacts (agrégation) :
 * - /contacts : vue calculée à chaque request, dédup fragile par email/nom
 * - /parties  : entités stockées avec ID stable, dedup_key robuste, backlinks
 *   vers les sources d'origine, prêt à devenir source de vérité éditoriale
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Users2, RefreshCw, Loader2, Search, Building2, User, MapPin, Mail,
  Phone, ExternalLink, X, GitMerge,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PartyDetailPanel } from "../components/parties/PartyDetailPanel";
import { DuplicatesPanel } from "../components/parties/DuplicatesPanel";

const MODULE_LABELS = {
  distributor: "Automate",
  reseller: "Revendeur",
  shop_customer: "Client B2C",
  catering_quote: "Traiteur",
  reseller_application: "Prospect",
  company_letter: "Courrier",
  standalone_contact: "Autonome",
};

const MODULE_TONES = {
  distributor: "bg-emerald-50 text-emerald-800 border-emerald-200",
  reseller: "bg-amber-50 text-amber-900 border-amber-200",
  shop_customer: "bg-blue-50 text-blue-800 border-blue-200",
  catering_quote: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  reseller_application: "bg-slate-50 text-slate-700 border-slate-200",
  company_letter: "bg-orange-50 text-orange-800 border-orange-200",
  standalone_contact: "bg-neutral-50 text-neutral-700 border-neutral-200",
};

export default function Parties() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("__all__");
  const [linkedModule, setLinkedModule] = useState("__all__");
  const [offset, setOffset] = useState(0);
  const [selectedParty, setSelectedParty] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [propagating, setPropagating] = useState(false);
  const [dupesOpen, setDupesOpen] = useState(false);
  const LIMIT = 50;

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, offset };
      if (search.trim()) params.search = search.trim();
      if (kind !== "__all__") params.kind = kind;
      if (linkedModule !== "__all__") params.linked_module = linkedModule;
      const [listRes, statsRes] = await Promise.all([
        api.get("/admin/parties", { params }),
        api.get("/admin/parties/stats"),
      ]);
      setItems(listRes.data.items);
      setTotal(listRes.data.total);
      setStats(statsRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [offset, kind, linkedModule]);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/admin/parties/backfill");
      toast.success(
        `✅ Backfill terminé : ${data.total_created} nouvelles Parties, ${data.total_scanned} sources scannées`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur backfill");
    } finally {
      setBackfilling(false);
    }
  };

  const runPropagateIds = async () => {
    setPropagating(true);
    try {
      const { data } = await api.post("/admin/parties/backfill-source-ids");
      toast.success(
        `✅ ${data.total_updated} sources hydratées avec party_id (${data.total_matched} inspectées)`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur propagation");
    } finally {
      setPropagating(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setKind("__all__");
    setLinkedModule("__all__");
    setOffset(0);
    setTimeout(load, 100);
  };

  return (
    <div className="p-6 space-y-6" data-testid="parties-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1C1A]">Carnet central — Parties</h1>
          <p className="text-sm text-[#575A57] mt-1 max-w-2xl">
            Vague 1 de la refonte relationnelle : fondation centrale des adresses.
            Chaque personne ou organisation a un ID unique et pointe vers ses sources
            (distributeur, revendeur, devis…).
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setDupesOpen(true)}
            variant="outline"
            className="border-[#8C3A2A] text-[#8C3A2A] hover:bg-[#FDF3EA]"
            data-testid="parties-duplicates-btn"
            title="Vague 4 — détecte et fusionne les Parties partageant un même dedup_key"
          >
            <GitMerge className="w-4 h-4 mr-2" />
            Détecter doublons
          </Button>
          <Button
            onClick={runPropagateIds}
            disabled={propagating}
            variant="outline"
            className="border-[#8C6B2F] text-[#8C6B2F] hover:bg-[#FBF4E9]"
            data-testid="parties-propagate-ids-btn"
            title="Vague 2 — écrit party_id sur les collections sources (idempotent)"
          >
            {propagating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {propagating ? "Propagation…" : "Propager party_id vers sources"}
          </Button>
          <Button
            onClick={runBackfill}
            disabled={backfilling}
            className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white"
            data-testid="parties-backfill-btn"
          >
            {backfilling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {backfilling ? "Backfill…" : "Lancer un backfill"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <Card className="border-[#C5DBCF] bg-gradient-to-br from-[#F5F9F6] via-[#FBF4E9] to-[#F5F9F6]" data-testid="parties-stats-card">
          <CardContent className="p-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
              <StatBlock label="Total" value={stats.total} icon={Users2} />
              <StatBlock label="Personnes" value={stats.by_kind.person} icon={User} />
              <StatBlock label="Organisations" value={stats.by_kind.organization} icon={Building2} />
              <StatBlock label="Orphelines" value={stats.orphans} muted />
              <StatBlock label="Doublons potentiels" value={stats.duplicate_groups} muted={stats.duplicate_groups === 0} warn={stats.duplicate_groups > 0} />
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {Object.entries(stats.by_module).map(([mod, count]) => (
                <button
                  key={mod}
                  onClick={() => { setLinkedModule(mod); setOffset(0); }}
                  className={`text-xs px-2 py-1 rounded border ${MODULE_TONES[mod] || ""} hover:opacity-80`}
                  data-testid={`parties-stat-module-${mod}`}
                >
                  {MODULE_LABELS[mod] || mod} · {count}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtres */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px] relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#A3A6A3] pointer-events-none" />
              <Input
                placeholder="Rechercher nom, email, ville…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setOffset(0); load(); } }}
                className="pl-8"
                data-testid="parties-filter-search"
              />
            </div>
            <Select value={kind} onValueChange={(v) => { setKind(v); setOffset(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="parties-filter-kind">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tous types</SelectItem>
                <SelectItem value="person">Personnes</SelectItem>
                <SelectItem value="organization">Organisations</SelectItem>
              </SelectContent>
            </Select>
            <Select value={linkedModule} onValueChange={(v) => { setLinkedModule(v); setOffset(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="parties-filter-module">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes sources</SelectItem>
                {Object.entries(MODULE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => { setOffset(0); load(); }} disabled={loading} className="bg-[#1F4E3D] hover:bg-[#173B2E] text-white">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Chercher
            </Button>
            {(search || kind !== "__all__" || linkedModule !== "__all__") && (
              <Button variant="outline" onClick={clearFilters}>
                <X className="w-3 h-3 mr-1" /> Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Sources liées</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-[#A3A6A3]">
                    Aucune Party — lance un backfill pour peupler depuis les collections sources.
                  </TableCell>
                </TableRow>
              )}
              {items.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer hover:bg-[#FAFAF7]"
                  onClick={() => setSelectedParty(p)}
                  data-testid={`party-row-${p.id}`}
                >
                  <TableCell>
                    <div className="font-medium text-[#1A1C1A]">{p.display_name}</div>
                    {p.legal_name && p.legal_name !== p.display_name && (
                      <div className="text-xs text-[#575A57]">{p.legal_name}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {p.kind === "organization" ? <><Building2 className="w-3 h-3 mr-1" /> Org</> : <><User className="w-3 h-3 mr-1" /> Pers</>}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="text-xs space-y-0.5">
                      {p.email && <div className="flex items-center gap-1 text-[#1F4E3D]"><Mail className="w-3 h-3" /> {p.email}</div>}
                      {p.phone && <div className="flex items-center gap-1 text-[#575A57]"><Phone className="w-3 h-3" /> {p.phone}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {p.city && (
                      <div className="flex items-center gap-1 text-xs text-[#575A57]">
                        <MapPin className="w-3 h-3" /> {[p.postal_code, p.city].filter(Boolean).join(" ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.linked_sources || []).slice(0, 3).map((s, i) => (
                        <span
                          key={`${s.module}-${s.id}-${i}`}
                          className={`text-[10px] px-1.5 py-0.5 rounded border ${MODULE_TONES[s.module] || ""}`}
                          title={`${s.module} · ${s.id}`}
                        >
                          {MODULE_LABELS[s.module] || s.module}
                        </span>
                      ))}
                      {(p.linked_sources || []).length > 3 && (
                        <span className="text-[10px] text-[#A3A6A3]">+{p.linked_sources.length - 3}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex items-center gap-1 text-[#1F4E3D] text-sm">
                      Détail <ExternalLink className="w-3 h-3" />
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {total > LIMIT && (
            <div className="p-3 flex items-center justify-between text-sm border-t border-[#E5E5E0]">
              <span className="text-[#575A57]">
                {offset + 1}–{Math.min(offset + LIMIT, total)} sur {total}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                >
                  Précédent
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + LIMIT >= total || loading}
                  onClick={() => setOffset(offset + LIMIT)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedParty && (
        <PartyDetailPanel
          partyId={selectedParty.id}
          open={Boolean(selectedParty)}
          onClose={() => setSelectedParty(null)}
          onSaved={() => { setSelectedParty(null); load(); }}
        />
      )}

      <DuplicatesPanel
        open={dupesOpen}
        onClose={() => setDupesOpen(false)}
        onMerged={() => load()}
      />
    </div>
  );
}

function StatBlock({ label, value, icon: Icon, muted, warn }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-[#575A57] flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`font-mono text-2xl mt-1 leading-tight ${
        warn ? "text-[#8C6B2F]" : muted ? "text-[#A3A6A3]" : "text-[#1F4E3D]"
      }`}>
        {value ?? 0}
      </div>
    </div>
  );
}
